import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { resendSignUpCode, resetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FaArrowLeftLong } from 'react-icons/fa6';
import {
    PROFILE_RESET_PASSWORD_PATH,
    LOGIN_PATH,
    VERIFICATION_PATH,
} from '@constants/routes';
import { setLoginEmail } from '@store/reducers/auth/loginSlice';
import { checkUserExists } from '@utils/utils';
import ErrorMessage from '@components/Error/ErrorMessage';
import LoadingSpinner from '@components/Spinner';
import { setEmail, setShowLink } from '@store/reducers/auth/signupSlice';

const ChangePassword = () => {
    const [userEmail, setUserEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const loginEmail = useAppSelector((state) => state.login.email);

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        const message = 'Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
    }, []);

    // useEffect(() => {
    //     window.addEventListener('beforeunload', handleBeforeUnload);

    //     setUserEmail(loginEmail);

    //     return () => {
    //         window.removeEventListener('beforeunload', handleBeforeUnload);
    //     };
    // }, [loginEmail, handleBeforeUnload]);

    const handleUserEmailChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setUserEmail(event.target.value);
        setError(null);
    };

    const handleForgotPassword = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            await checkUserExists(userEmail, false);

            await resetPassword({ username: userEmail });

            dispatch(setLoginEmail(userEmail));
            navigate(PROFILE_RESET_PASSWORD_PATH);
        } catch (error: any) {
            // console.log(error);
            let errorMessage = 'Internal Server Error';

            switch (error.name) {
                case 'UserNotFoundException':
                case 'NotAuthorizedException':
                    errorMessage = 'Invalid credential.';
                    break;
                case 'UserNotConfirmedException':
                    await resendSignUpCode({
                        username: userEmail,
                    });
                    dispatch(setEmail(userEmail));
                    dispatch(setShowLink(false));
                    navigate(VERIFICATION_PATH);
                    errorMessage = 'User not Verified.';
                case 'TooManyRequestsException':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Invalid parameters provided.';
                    break;
                default:
                    errorMessage = error.message || 'An unknown error occurred';
                    break;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const backArrow = () => {
        navigate(-1);
    };

    return (
            <>
                <div className="flex font-bold text-base text-black items-center" onClick={backArrow}>
                    <FaArrowLeftLong className="" />{' '}
                    <h2 className="font-bold ml-2">{t('auth.back')}</h2>
                </div>
                <div className="flex flex-col items-center justify-center mt-5">
                    <div className="card 2xl:w-[34%] lg:w-[60%] xl:w-[45%] border-none rounded-2xl mb-3">
                        <div className="card-body p-5">
                            <h4 className="text-lg font-semibold text-oxford_blue mb-2">
                                {t('auth.reset_password.title')}
                            </h4>

                            <p className="text-grey test-sm font-normal mb-4">
                                {t('auth.forgot_password.description')}
                                <br />
                                {t('auth.forgot_password.description_1')}
                                <br />
                                {t('auth.forgot_password.description_2')}
                                <br />
                            </p>

                            {error && <ErrorMessage message={error} />}
                            <form onSubmit={handleForgotPassword}>
                                <div className="">
                                    <label
                                        htmlFor="email"
                                        className="form-label text-light_grey text-sm font-medium mb-2"
                                    >
                                        {t('auth.forgot_password.email_label')}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control px-3 py-2 placeholder-gains_boro rounded-lg"
                                        id="email"
                                        name="email"
                                        value={userEmail}
                                        onChange={handleUserEmailChange}
                                        placeholder={t(
                                            'auth.forgot_password.email_placeholder'
                                        )}
                                        autoFocus
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="mt-4">
                                    {isLoading ? (
                                        <div className="btn !bg-primary text-white text-base font-semibold py-2.5 w-full">
                                            <div>
                                                <LoadingSpinner className="text-secondary" />
                                            </div>
                                        </div>
                                    ) : (
                                        <button className="btn !bg-primary text-white text-base font-semibold py-2.5 w-full">
                                            {t('auth.forgot_password.confirm')}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </>
    );
};

export default ChangePassword;
